/**
 * @fsd_page AppHome - главная
 */

import s from './AppHome.module.sass'

import dynamic from 'next/dynamic'
import { useMemo } from 'react'
import { useAppSelector, useInjectPageReducer } from '@app/model/store/client'

import { selectAppHomeSections } from '@pages/AppHome/model/selectors'

import { RestaurantHomeFilters } from '@pages/AppHome/ui/RestaurantHomeFilters'
import { TagsRoll } from '@pages/AppHome/ui/TagsRoll'

import AppHomeReducer from './model/slice'
import {
  resolveDataSection,
  resolveNewsSection,
  resolveQuickFilterSection,
  resolveRestaurantSection,
  resolveSelectionSection,
  resolveSplitNewsSection,
  resolveTagSection,
} from '@pages/AppHome/model/helpers'

import { CompilationGrid } from '@widgets/News/CompilationGrid'
import { NewsCard } from '@widgets/News/NewsCard'
import { NewsCardCover } from '@widgets/News/NewsCardCover'
import { RestaurantMainCards } from '@widgets/Restaurant/RestaurantMainCards'
import { RestCard, RestCardSkeleton } from '@widgets/Restaurant/RestCards/RestCard'
import { SelectionCardCompilation, SelectionCardCompilationSkeleton } from '@widgets/Selection/SelectionCardCompilation'
import WeekendGuide from '@widgets/Common/WeekendGuide/WeekendGuide'

import { PromoRoll } from '@features/PromoRoll'

import { AdvertTemplate, SidebarSticky } from '@shared/ui/Layout/AdvertTemplate'
import { BlockBoundary } from '@shared/lib/components/BlockBoundary'
import { ContainerLongLeft } from '@shared/ui/Layout/Containers/ContainerLongLeft'
import { ContainerTwoColumns } from '@shared/ui/Layout/Containers/ContainerTwoColumns'
import { log, logSSR } from '@shared/lib/helpers/debugLog'
import { SectionCarousel } from '@shared/ui/Layout/SectionCarousel'
import { useClientResponsive } from '@shared/lib/hooks/useClientResponsive'
import KUPER_CONST from '@shared/consts/kuper'
import SectionTitle from '@shared/ui/Layout/SectionTitle/SectionTitle'
import useKuperFlags from '@shared/lib/hooks/useKuperFlags'
import { safeArrayLength } from '@shared/lib/helpers/safeArrayLength'
import type { AppHomeServerData } from '@pages/AppHome/model/types'
import type { TypeMappingRestTag } from '@shared/api/middleware/mappingAPI/restaurant/type'

const DailyCarousel = dynamic(
  () => import('@widgets/Common/Daily/DailyCarousel').then((module) => module.DailyCarousel),
  { ssr: false, loading: () => null },
)

const GigAssChatSection = dynamic(
  () => import('@widgets/Common/GigAssist/components/GigAssChatSection').then((module) => module.GigAssChatSection),
  { ssr: false, loading: () => null },
)

const KUPER_TOKEN = process.env.KUPER_TOKEN || ''

// создание массива скелетонов для карусели
const createSkeletons = (
  count = 4,
  sizes = 'L ML',
  SkeletonComponent: React.ComponentType<{ sizes: string }> = RestCardSkeleton,
) => {
  const n = safeArrayLength(count, 50, 4)
  return Array.from(Array(n).keys()).map((_, idx) => {
    return <SkeletonComponent key={`skeleton-${idx}`} sizes={sizes} />
  })
}

type AppHomeProps = {
  serverData?: AppHomeServerData
  requestPath?: string
  filtersTitleTag?: 'h1' | 'h2'
  weekendGuide?:
    import('@shared/api/middleware/mappingAPI/gql_weekend-guide/type').TypeMappingWeekendGuideGQLOutput | null
}

export default function AppHome({
  serverData,
  requestPath = '',
  filtersTitleTag = 'h1',
  weekendGuide = null,
}: AppHomeProps) {
  useInjectPageReducer('app_home', AppHomeReducer)

  const { isMobile, isTablet, isDesktopLarge } = useClientResponsive()

  const currentCity = useAppSelector((state) => state.global.currentCity)
  const citySlugFallback = requestPath?.split('/').filter(Boolean)[0] ?? ''
  const citySlug = currentCity?.slug ?? citySlugFallback
  const currentCitySlug = citySlug

  const stateHome = useAppSelector((state) => state?.pages?.app_home)

  const {
    featureRests,
    tags,
    quickFilters,
    bookingRests,
    specialRests,
    deliveryKuperRests,
    ratingRests,
    afishaSelections,
    weekPlans,
    restaurantsNews,
    otherNews,
    interviews,
  } = useAppSelector(selectAppHomeSections)

  const restaurantFiltersReady = useAppSelector((store) => store.features?.restaurant_filters?.ready ?? false)
  const restaurantFilterCatalogsReady = useAppSelector(
    (store) => store.features?.restaurant_filters?.filterCatalogsReady ?? false,
  )
  const hasCityReadCoordinates = Boolean(useAppSelector((store) => store.global.cityRead?.coordinates))
  const storeLoaded = useAppSelector((store) => store.global.storeLoaded ?? false)

  const resolvedFeatureRests = resolveRestaurantSection(serverData?.feature_rests, featureRests)
  const resolvedTags = resolveTagSection(serverData?.tags, tags)
  const resolvedQuickFilters = resolveQuickFilterSection(serverData?.quick_filters, quickFilters)
  const resolvedBookingRests = resolveRestaurantSection(serverData?.booking_rests, bookingRests)
  const resolvedSpecialRests = resolveRestaurantSection(serverData?.special_offer, specialRests)
  const resolvedDeliveryKuperRests = resolveRestaurantSection(serverData?.delivery_kuper, deliveryKuperRests)
  const resolvedRatingRests = resolveRestaurantSection(serverData?.by_rating, ratingRests)
  const resolvedAfishaSelections = resolveSelectionSection(serverData?.afisha_selected, afishaSelections)
  const resolvedWeekPlans = resolveDataSection(serverData?.plani_na_nedelu, weekPlans)
  const resolvedRestaurantsNews = resolveSplitNewsSection(serverData?.all_data, restaurantsNews, 'even')
  const resolvedOtherNews = resolveSplitNewsSection(serverData?.all_data, otherNews, 'odd')
  const resolvedInterviews = resolveNewsSection(serverData?.interviews, interviews)

  const tagsFormatted = useMemo(() => {
    if (!resolvedTags.list?.length) return []

    return resolvedTags.list
      .map((tag) => tag.tag_object)
      .filter((obj): obj is NonNullable<TypeMappingRestTag['tag_object']> => {
        return !!obj && typeof obj.id === 'number' && !!obj.name && !!obj.slug && typeof obj.abs_url === 'string'
      })
  }, [resolvedTags.list])

  const activeMainCardsSkeleton = resolvedFeatureRests.loading
  const activeTagsRollSkeleton = resolvedTags.loading

  log('AppHome', 'stateHome', {
    hasAppHome: !!stateHome,
    featureRestsLoading: resolvedFeatureRests?.loading,
    featureRestsCount: resolvedFeatureRests?.list?.length ?? 0,
  })

  const showMainSection = Boolean(
    activeMainCardsSkeleton || activeTagsRollSkeleton || resolvedFeatureRests.list?.length || resolvedTags.list?.length,
  )
  const showSectionSpecialRests = Boolean(resolvedSpecialRests.loading || resolvedSpecialRests.list?.length)
  const showBookingRestsInBigCity = Boolean(
    ['msk', 'spb'].includes(currentCitySlug) && !resolvedBookingRests.loading && resolvedBookingRests.list?.length,
  )
  const showBookingRestsInSmallCity = Boolean(
    !['msk', 'spb'].includes(currentCitySlug) && !resolvedBookingRests.loading && resolvedBookingRests.list?.length,
  )
  const showSectionNewsInteresting = Boolean(resolvedRestaurantsNews.loading || resolvedRestaurantsNews.list?.length)
  const showInterviews = Boolean(resolvedInterviews.loading || resolvedInterviews.list?.length)
  const showSectionRecommended = Boolean(resolvedRatingRests.loading || resolvedRatingRests.list?.length)
  const showAllNews = Boolean(resolvedOtherNews.loading || resolvedOtherNews.list?.length)
  const showAfishaSelected = Boolean(resolvedAfishaSelections.loading || resolvedAfishaSelections.list?.length)
  const showWeekPlans = Boolean(resolvedWeekPlans.loading || !!resolvedWeekPlans?.data)

  const promoIds = ['guide', 'possibilities-restaurateurs', 'gastrogid']
  const [isProdActive, isDevActive] = useKuperFlags()
  const isActiveKuper = ['msk', 'spb'].includes(currentCitySlug) && (isProdActive || isDevActive)
  if (isActiveKuper) promoIds.unshift('kuper')

  const ssrSections = {
    mainSection: showMainSection,
    restaurantMainCards: (resolvedFeatureRests.list?.length ?? 0) > 0,
    tagsRoll: tagsFormatted.length > 0,
    promoRoll: true,
    restaurantHomeFilters: true,
    bookingRests: showBookingRestsInSmallCity || showBookingRestsInBigCity,
    specialRests: showSectionSpecialRests,
    newsInteresting: showSectionNewsInteresting,
    weekendGuide: true,
    interviews: showInterviews,
    recommended: showSectionRecommended,
    allNews: showAllNews,
    afishaSelected: showAfishaSelected,
    weekPlans: showWeekPlans,
  }
  logSSR({ tag: 'AppHome', pathOrFirst: requestPath, data: ['sections rendered', ssrSections] })

  // ключевые элементы из плана (12 блоков): хотя бы один в каждом списке
  const hasCitySource = Boolean(currentCity) || Boolean(citySlugFallback)
  const keyElementsOk = {
    header: {
      logo: storeLoaded,
      menu: storeLoaded,
      loginButton: storeLoaded,
      searchButton: storeLoaded && !isMobile,
    },
    restaurantMainCards: (resolvedFeatureRests.list?.length ?? 0) >= 1 && !activeMainCardsSkeleton,
    tagsRoll: tagsFormatted.length >= 1 && !activeTagsRollSkeleton,
    promoRoll: true,
    restaurantHomeFilters:
      hasCitySource && (resolvedQuickFilters.list?.length ?? 0) >= 1 && !resolvedQuickFilters.loading,
    restaurantFilters: restaurantFilterCatalogsReady && hasCityReadCoordinates,
    quickFilters: (resolvedQuickFilters.list?.length ?? 0) >= 1 && !resolvedQuickFilters.loading,
    bookingRests: (resolvedBookingRests.list?.length ?? 0) >= 1 && !resolvedBookingRests.loading,
    specialRests: (resolvedSpecialRests.list?.length ?? 0) >= 1 && !resolvedSpecialRests.loading,
    newsInteresting: (resolvedRestaurantsNews.list?.length ?? 0) + (resolvedOtherNews.list?.length ?? 0) >= 1,
    weekendGuide: true,
    interviews: (resolvedInterviews.list?.length ?? 0) >= 1 && !resolvedInterviews.loading,
    recommended: (resolvedRatingRests.list?.length ?? 0) >= 1 && !resolvedRatingRests.loading,
    afishaSelected: (resolvedAfishaSelections.list?.length ?? 0) >= 1 && !resolvedAfishaSelections.loading,
    weekPlans: !!resolvedWeekPlans?.data && !resolvedWeekPlans.loading,
    footer: true,
  }

  logSSR({ tag: 'AppHome', pathOrFirst: requestPath, data: ['key elements (plan, at least 1 item)', keyElementsOk] })

  // детальное логирование: счётчики и флаги skeleton для отладки no-JS
  const ssrDetail = {
    fromServerData: !!serverData,
    header: {
      storeLoaded,
      logo: storeLoaded,
      menu: storeLoaded,
      loginButton: storeLoaded,
      searchButton: storeLoaded && !isMobile,
      skeleton: !storeLoaded,
      hasContent: storeLoaded,
    },
    mainCards: {
      listCount: resolvedFeatureRests.list?.length ?? 0,
      skeleton: activeMainCardsSkeleton,
      hasContent: (resolvedFeatureRests.list?.length ?? 0) > 0 && !activeMainCardsSkeleton,
    },
    tags: {
      listCount: tagsFormatted.length,
      skeleton: activeTagsRollSkeleton,
      hasContent: tagsFormatted.length > 0 && !activeTagsRollSkeleton,
    },
    quickFilters: {
      listCount: resolvedQuickFilters.list?.length ?? 0,
      skeleton: resolvedQuickFilters.loading,
      hasContent: (resolvedQuickFilters.list?.length ?? 0) > 0 && !resolvedQuickFilters.loading,
    },
    restaurantHomeFilters: {
      hasCitySource,
      quickFiltersCount: resolvedQuickFilters.list?.length ?? 0,
      hasContent: hasCitySource && (resolvedQuickFilters.list?.length ?? 0) > 0 && !resolvedQuickFilters.loading,
    },
    restaurantFilters: {
      ready: restaurantFiltersReady,
      filterCatalogsReady: restaurantFilterCatalogsReady,
      hasCoordinates: hasCityReadCoordinates,
      hasContent: restaurantFilterCatalogsReady && hasCityReadCoordinates,
    },
    weekendGuide: { hasContent: true },
    bookingRests: {
      listCount: resolvedBookingRests.list?.length ?? 0,
      skeleton: resolvedBookingRests.loading,
      hasContent: (resolvedBookingRests.list?.length ?? 0) > 0 && !resolvedBookingRests.loading,
    },
    specialRests: {
      listCount: resolvedSpecialRests.list?.length ?? 0,
      skeleton: resolvedSpecialRests.loading,
      hasContent: (resolvedSpecialRests.list?.length ?? 0) > 0 && !resolvedSpecialRests.loading,
    },
    newsInteresting: {
      listCount: (resolvedRestaurantsNews.list?.length ?? 0) + (resolvedOtherNews.list?.length ?? 0),
      skeleton: resolvedRestaurantsNews.loading,
      hasContent: (resolvedRestaurantsNews.list?.length ?? 0) + (resolvedOtherNews.list?.length ?? 0) > 0,
    },
    interviews: {
      listCount: resolvedInterviews.list?.length ?? 0,
      skeleton: resolvedInterviews.loading,
      hasContent: (resolvedInterviews.list?.length ?? 0) > 0 && !resolvedInterviews.loading,
    },
    recommended: {
      listCount: resolvedRatingRests.list?.length ?? 0,
      skeleton: resolvedRatingRests.loading,
      hasContent: (resolvedRatingRests.list?.length ?? 0) > 0 && !resolvedRatingRests.loading,
    },
    afishaSelected: {
      listCount: resolvedAfishaSelections.list?.length ?? 0,
      skeleton: resolvedAfishaSelections.loading,
      hasContent: (resolvedAfishaSelections.list?.length ?? 0) > 0 && !resolvedAfishaSelections.loading,
    },
    allNews: {
      listCount: resolvedOtherNews.list?.length ?? 0,
      skeleton: resolvedOtherNews.loading,
      hasContent: (resolvedOtherNews.list?.length ?? 0) > 0 && !resolvedOtherNews.loading,
    },
    weekPlans: {
      hasData: !!resolvedWeekPlans?.data,
      skeleton: resolvedWeekPlans.loading,
      hasContent: !!resolvedWeekPlans?.data && !resolvedWeekPlans.loading,
    },
  }

  logSSR({ tag: 'AppHome', pathOrFirst: requestPath, data: ['detail (counts, skeleton, hasContent)', ssrDetail] })

  return (
    <BlockBoundary>
      <main>
        {isMobile && (
          <ContainerLongLeft>
            <RestaurantHomeFilters
              requestPath={requestPath}
              className={s.filters}
              quickFilters={resolvedQuickFilters.list}
              quickFiltersLoading={resolvedQuickFilters.loading}
              titleTag={filtersTitleTag}
            />
          </ContainerLongLeft>
        )}

        {showMainSection && (
          <div className={s['main-section']}>
            <>
              <RestaurantMainCards list={resolvedFeatureRests.list || []} skeleton={activeMainCardsSkeleton} />
            </>

            <TagsRoll tags={tagsFormatted} skeleton={activeTagsRollSkeleton} citySlug={currentCitySlug} />
          </div>
        )}

        <div className={s['container-main-wrapper']}>
          {/* Маркетинговые блоки */}
          <PromoRoll promoIds={promoIds} />

          {!isMobile && !isDesktopLarge && (
            <ContainerLongLeft>
              <RestaurantHomeFilters
                requestPath={requestPath}
                className={s.filters}
                quickFilters={resolvedQuickFilters.list}
                quickFiltersLoading={resolvedQuickFilters.loading}
                titleTag={filtersTitleTag}
              />
            </ContainerLongLeft>
          )}

          <ContainerTwoColumns>
            <ContainerTwoColumns.Main className={s['container-main']}>
              {isDesktopLarge && (
                <RestaurantHomeFilters
                  requestPath={requestPath}
                  className={s.filters}
                  quickFilters={resolvedQuickFilters.list}
                  quickFiltersLoading={resolvedQuickFilters.loading}
                  titleTag={filtersTitleTag}
                />
              )}

              {showBookingRestsInSmallCity && (
                <SectionCarousel
                  countSlidesOnContainer={3}
                  title={'Бронируйте прямо сейчас'}
                  link={`/${citySlug}/restaurants/restaurant_list/has_booking/`}
                  className={s['booking-now-rests-slider-roll']}
                  skeleton={resolvedBookingRests.loading}>
                  <SectionCarousel.Items>
                    {resolvedBookingRests.list
                      ? [
                          ...resolvedBookingRests.list.map((item, index) => (
                            <RestCard
                              key={item.id || index}
                              data={item}
                              sizes={'L ML'}
                              type={'booking'}
                              analytic={{
                                advertCardPlaceType: null,
                                section_name: `Бронируйте прямо сейчас`,
                                section_index: index,
                              }}
                            />
                          )),
                        ]
                      : []}
                  </SectionCarousel.Items>
                  <SectionCarousel.Skeletons>{createSkeletons()}</SectionCarousel.Skeletons>
                </SectionCarousel>
              )}

              {showBookingRestsInBigCity && (
                <SectionCarousel
                  countSlidesOnContainer={3}
                  title={'Бронируйте прямо сейчас'}
                  link={`/${citySlug}/restaurants/${
                    citySlug === 'spb'
                      ? 'restorany-kotorye-nado-bronirovat-pryamo-sejchas-2025'
                      : 'restorany-kotorye-nado-bronirovat-pryamo-sejchas'
                  }/`}
                  className={s['booking-now-rests-slider-roll']}
                  skeleton={resolvedBookingRests.loading}>
                  <SectionCarousel.Items>
                    {resolvedBookingRests.list
                      ? [
                          ...resolvedBookingRests.list.map((item, index) => (
                            <RestCard
                              key={item.id || index}
                              data={item}
                              sizes={'L ML'}
                              type={'booking'}
                              analytic={{
                                advertCardPlaceType: null,
                                section_name: `Бронируйте прямо сейчас`,
                                section_index: index,
                              }}
                            />
                          )),
                        ]
                      : []}
                  </SectionCarousel.Items>

                  <SectionCarousel.Skeletons>{createSkeletons()}</SectionCarousel.Skeletons>
                </SectionCarousel>
              )}

              {/* Спецпредложения, (рестораны) */}

              {showSectionSpecialRests && (
                <SectionCarousel
                  countSlidesOnContainer={3}
                  title={'Спецпредложения'}
                  link={`/${citySlug}/restaurants/restaurant_list/owner_offer/`}
                  className={s['special-rests-slider-roll']}
                  skeleton={resolvedSpecialRests.loading}>
                  <SectionCarousel.Items>
                    {resolvedSpecialRests.list
                      ? [
                          ...resolvedSpecialRests.list.map((item, index) => (
                            <RestCard
                              key={item.id || index}
                              data={item}
                              sizes={'L ML'}
                              type={'special-offer'}
                              analytic={{
                                advertCardPlaceType: null,
                                section_name: `Спецпредложения`,
                                section_index: index,
                              }}
                            />
                          )),
                        ]
                      : []}
                  </SectionCarousel.Items>
                  <SectionCarousel.Skeletons>{createSkeletons()}</SectionCarousel.Skeletons>
                </SectionCarousel>
              )}

              {/* Самое интересное про рестораны, (новости) */}
              {showSectionNewsInteresting && (
                <SectionCarousel
                  countSlidesOnContainer={2}
                  title={'Самое интересное про рестораны'}
                  link={`/${citySlug}/restaurant-news/`}
                  skeleton={resolvedRestaurantsNews.loading}>
                  <SectionCarousel.Items>
                    {resolvedRestaurantsNews.list
                      ? [
                          ...resolvedRestaurantsNews.list.map((news, index) => (
                            <NewsCard
                              key={news.id || index}
                              sizes={(index + 1) % 3 === 0 ? 'L2x1 M2x1 M2x1 1x1' : 'L1x1 M1x1 M1x1 1x1'}
                              data={news}
                              analytic={{
                                section_name: `Самое интересное про рестораны`,
                                section_index: index,
                              }}
                            />
                          )),
                        ]
                      : []}
                  </SectionCarousel.Items>
                  <SectionCarousel.Skeletons>{createSkeletons()}</SectionCarousel.Skeletons>
                </SectionCarousel>
              )}

              <GigAssChatSection />

              <WeekendGuide initial={weekendGuide} />

              {showInterviews && (
                <SectionCarousel
                  countSlidesOnContainer={2}
                  title={'Интервью'}
                  link={`/${citySlug}/restaurant-news/?variety=interviu`}
                  skeleton={resolvedInterviews.loading}>
                  <SectionCarousel.Items>
                    {resolvedInterviews.list
                      ? [
                          ...resolvedInterviews.list.map((news, index) => (
                            <NewsCard
                              key={news.id || index}
                              sizes={(index + 2) % 3 === 0 ? 'L2x1 M2x1 M2x1 1x1' : 'L1x1 M1x1 M1x1 1x1'}
                              data={news}
                              analytic={{
                                section_name: `Интервью`,
                                section_index: index,
                              }}
                            />
                          )),
                        ]
                      : []}
                  </SectionCarousel.Items>
                  <SectionCarousel.Skeletons>{createSkeletons()}</SectionCarousel.Skeletons>
                </SectionCarousel>
              )}

              {/*Рекомендации для вас, (рестораны) */}
              {showSectionRecommended && (
                <SectionCarousel
                  countSlidesOnContainer={3}
                  title={'Рекомендации для вас'}
                  className={s['by-rating-rests-slider-roll']}
                  skeleton={resolvedRatingRests.loading}>
                  <SectionCarousel.Items>
                    {resolvedRatingRests.list
                      ? [
                          ...resolvedRatingRests.list.map((item, index) => (
                            <RestCard
                              key={item.id || index}
                              data={item}
                              sizes={'L ML'}
                              type={'list'}
                              analytic={{
                                advertCardPlaceType: null,
                                section_name: `Рекомендации`,
                                section_index: index,
                              }}
                            />
                          )),
                        ]
                      : []}
                  </SectionCarousel.Items>
                  <SectionCarousel.Skeletons>{createSkeletons()}</SectionCarousel.Skeletons>
                </SectionCarousel>
              )}

              <AdvertTemplate type={'100%x240 Listing1'} />

              {/* Статьи, новости, обзоры (новости) */}
              {showAllNews && (
                <SectionCarousel
                  countSlidesOnContainer={3}
                  title={'Статьи, новости, обзоры'}
                  link={`/${citySlug}/restaurant-news/`}
                  skeleton={resolvedOtherNews.loading}>
                  <SectionCarousel.Items>
                    {resolvedOtherNews.list
                      ? [
                          ...resolvedOtherNews.list.map((news, index) => (
                            <NewsCard
                              key={news.id || index}
                              sizes={index % 3 === 0 ? 'L2x1 M2x1 M2x1 1x1' : 'L1x1 M1x1 M1x1 1x1'}
                              data={news}
                              analytic={{
                                section_name: `Статьи, новости, обзоры`,
                                section_index: index,
                              }}
                            />
                          )),
                        ]
                      : []}
                  </SectionCarousel.Items>
                  <SectionCarousel.Skeletons>{createSkeletons()}</SectionCarousel.Skeletons>
                </SectionCarousel>
              )}

              {/* Заказать доставку в Купер */}
              {Boolean(resolvedDeliveryKuperRests.list?.length) && isActiveKuper && (
                <SectionCarousel
                  countSlidesOnContainer={3}
                  title={isMobile ? 'Заказать доставку' : 'Заказать доставку в Купер'}
                  link={`/${citySlug}/restaurants/restaurant_list/delivery_kuper/`}
                  className={s['delivery-rests-slider-roll']}
                  kuper={{
                    description: KUPER_CONST.description.long,
                    token: KUPER_TOKEN,
                  }}
                  skeleton={resolvedDeliveryKuperRests.loading}>
                  <SectionCarousel.Items>
                    {resolvedDeliveryKuperRests.list?.map((item, index) => (
                      <RestCard
                        key={item.id}
                        data={item}
                        sizes={'L ML'}
                        type={'list'}
                        analytic={{
                          advertCardPlaceType: null,
                          section_name: `Заказать доставку в Купер`,
                          section_index: index,
                        }}
                        advert={{
                          kuper: {
                            restName: item.name,
                            link: item.kuper.link,
                            token: KUPER_TOKEN,
                          },
                        }}
                      />
                    ))}
                  </SectionCarousel.Items>
                  <SectionCarousel.Skeletons>
                    {Array.from(Array(4).keys()).map((_, idx) => (
                      <RestCardSkeleton key={idx} sizes={'L ML'} />
                    ))}
                  </SectionCarousel.Skeletons>
                </SectionCarousel>
              )}
            </ContainerTwoColumns.Main>

            {!isMobile && !isTablet && (
              <ContainerTwoColumns.Aside>
                <AdvertTemplate type={'240x400'} container={SidebarSticky} />
                <AdvertTemplate type={'240x400_2nd'} container={SidebarSticky} />
              </ContainerTwoColumns.Aside>
            )}
          </ContainerTwoColumns>

          {/* Планы на неделю */}
          {showWeekPlans && resolvedWeekPlans?.data && (
            <ContainerLongLeft>
              <NewsCardCover
                data={resolvedWeekPlans.data}
                subtitles={[resolvedWeekPlans.data.readingTime]}
                analytic={{
                  section_index: 0,
                  section_name: `Планы на неделю`,
                }}
                skeleton={resolvedWeekPlans.loading}
              />
            </ContainerLongLeft>
          )}

          <AdvertTemplate type={'Parallax Footer'} container={ContainerLongLeft} />

          {/* Выбор Афиши */}
          {showAfishaSelected && (
            <ContainerLongLeft>
              {isMobile ? (
                <SectionCarousel
                  title={'Выбор Афиши'}
                  link={`/${citySlug}/restaurants/afisha_selected/`}
                  countSlidesOnContainer={3}
                  skeleton={resolvedAfishaSelections.loading}>
                  <SectionCarousel.Items>
                    {resolvedAfishaSelections.list
                      ? [
                          ...resolvedAfishaSelections.list.map((item, index) => (
                            <SelectionCardCompilation
                              data={item}
                              key={item.id}
                              sizes={'M1x1'}
                              analytic={{
                                section_name: `Выбор Афиши`,
                                section_index: index,
                              }}
                            />
                          )),
                        ]
                      : []}
                  </SectionCarousel.Items>
                  <SectionCarousel.Skeletons>
                    {createSkeletons(6, 'M1x1', SelectionCardCompilationSkeleton)}
                  </SectionCarousel.Skeletons>
                </SectionCarousel>
              ) : (
                <section className={s['home-selections-grid']}>
                  <SectionTitle
                    title={'Выбор Афиши'}
                    link={`/${citySlug}/restaurants/afisha_selected/`}
                    skeleton={resolvedAfishaSelections.loading}
                  />
                  <CompilationGrid
                    type={'selections'}
                    counts={'6 6 4'}
                    list={resolvedAfishaSelections.list || []}
                    analytic={{ section_name: `Выбор Афиши` }}
                    skeleton={resolvedAfishaSelections.loading}
                  />
                </section>
              )}
            </ContainerLongLeft>
          )}

          {/* Афиша Daily */}
          <DailyCarousel container={ContainerLongLeft} />

          {!isMobile && <AdvertTemplate type={'Context_full'} container={ContainerLongLeft} />}
        </div>
      </main>
    </BlockBoundary>
  )
}
